import { FC, useState } from 'react';

import Button from '../button';
import { LazyLoadImage } from '../lazy-load-image';
import styles from './roomstay-blog-customer-case-accordion.module.scss';
import ClippedBox from '../roomstay-blog-image-block/clipped-box';
import { useWindowSize } from 'usehooks-ts';
import classNames from 'classnames';

export interface CustomerCaseAccordionProps {
    theme?: 'default' | 'type-2';
    tag?: string;
    title?: string;
    description?: string;
    subDescription?: string;
    ctaButton?: {
        text?: string;
        href?: string;
    };

    accordion: {
        icon: string;
        text?: string;
        description?: string;
    }[];
}

const RoomStayBlogCustomerCaseAccordion: FC<CustomerCaseAccordionProps> = (props) => {
    const { theme, tag, title, description, subDescription, ctaButton, accordion } = props;
    
    const { width: windowWidth } = useWindowSize();
    const isSmallMobile = windowWidth <= 578;
    const isMobile = windowWidth <= 768;
    const isTablet = windowWidth <= 991;

    const [expandedIndices, setExpandedIndices] = useState<number[]>([]);

    const handleToggle = (index: number) => {
        setExpandedIndices((prevIndices) =>
            prevIndices.includes(index)
                ? prevIndices.filter((i) => i !== index)
                : [...prevIndices, index]
        );
    };

    return (
        <div className={`${styles.container} ${theme === 'type-2' ? styles.type2 : ''}`}>
            {theme === 'type-2' && (
                <div className={styles.background}>
                    <ClippedBox
                        configs={[]}
                        customPolygon="0% 0%, 0% 100%, 100% 0%, 0% 0%"
                        className={classNames(styles.background, styles.backgroundClipped)}
                        style={{
                            backgroundColor: '#f9f9fa',
                            maxHeight: isTablet ? 48 : 100,
                            transform: 'rotate(180deg)',
                            }}
                        />
                </div>
            )}
            <div className={styles.wrapperBox}>
                <div className={styles.boxLeft}>
                    <div className={styles.wrapperTag}>
                        <div className={styles.tag} dangerouslySetInnerHTML={{ __html: tag || '' }}></div>
                    </div>
                    <h2 className={styles.title} dangerouslySetInnerHTML={{ __html: title || '' }}></h2>
                    <div className={styles.description} dangerouslySetInnerHTML={{ __html: description || '' }}></div>
                    <div className={styles.subDescription} dangerouslySetInnerHTML={{ __html: subDescription || '' }}></div>
                    {ctaButton?.text && theme === 'type-2' && (
                        <Button className={`${styles.button} ${styles.buttonDesktop}`} type="primary" theme="default" size="default" htmlType="button" link={ctaButton?.href}>
                            {ctaButton?.text || 'Book a Demo'}
                        </Button>
                    )}
                </div>

                <div className={styles.boxRight}>
                    {accordion.map((item, index) => {
                        const isExpanded = expandedIndices.includes(index);

                        return (
                            <div key={index} className={`${styles.wrapperItem} ${isExpanded ? styles.expanded : ''}`} onClick={() => handleToggle(index)}>
                                <div className={styles.wrapperHeader}>
                                    <div className={styles.headerLeft}>
                                        <div className={styles.icon}>
                                            <LazyLoadImage className={styles.image} imageUrl={item.icon} alt="" objectFit="cover" />
                                        </div>

                                        <h3 className={styles.text} dangerouslySetInnerHTML={{ __html: item.text || '' }}></h3>
                                    </div>

                                    <div className={styles.headerRight}>
                                        <div className={`${styles.arrow} ${isExpanded ? styles.rotated : ''}`}>
                                            {theme === 'type-2' ? (
                                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" className={styles.plusMinusIcon}>
                                                    <path opacity="0.15" d="M22.125 12C22.125 14.0025 21.5312 15.9601 20.4186 17.6251C19.3061 19.2902 17.7248 20.5879 15.8747 21.3543C14.0246 22.1206 11.9888 22.3211 10.0247 21.9305C8.06066 21.5398 6.25656 20.5755 4.84055 19.1595C3.42454 17.7435 2.46023 15.9393 2.06955 13.9753C1.67888 12.0112 1.87939 9.97543 2.64572 8.12533C3.41206 6.27523 4.70981 4.69392 6.37486 3.58137C8.0399 2.46882 9.99747 1.875 12 1.875C14.6844 1.87798 17.258 2.94567 19.1562 4.84383C21.0543 6.74199 22.122 9.3156 22.125 12ZM19.875 12C19.875 10.4425 19.4131 8.91992 18.5478 7.62488C17.6825 6.32985 16.4526 5.32049 15.0136 4.72445C13.5747 4.12841 11.9913 3.97246 10.4637 4.27632C8.93607 4.58017 7.53288 5.3302 6.43154 6.43153C5.3302 7.53287 4.58018 8.93606 4.27632 10.4637C3.97246 11.9913 4.12841 13.5747 4.72445 15.0136C5.32049 16.4526 6.32985 17.6825 7.62489 18.5478C8.91993 19.4131 10.4425 19.875 12 19.875C14.0879 19.8728 16.0896 19.0424 17.566 17.566C19.0424 16.0896 19.8728 14.0879 19.875 12Z" fill="#FF0068"/>
                                                    <path d="M16.875 12C16.875 12.2984 16.7565 12.5845 16.5455 12.7955C16.3345 13.0065 16.0484 13.125 15.75 13.125H8.25C7.95163 13.125 7.66548 13.0065 7.4545 12.7955C7.24353 12.5845 7.125 12.2984 7.125 12C7.125 11.7016 7.24353 11.4155 7.4545 11.2045C7.66548 10.9935 7.95163 10.875 8.25 10.875H15.75C16.0484 10.875 16.3345 10.9935 16.5455 11.2045C16.7565 11.4155 16.875 11.7016 16.875 12Z" fill="#FF0068"/>
                                                    <path 
                                                        d="M12 16.875C11.7016 16.875 11.4155 16.7565 11.2045 16.5455C10.9935 16.3345 10.875 16.0484 10.875 15.75L10.875 8.25C10.875 7.95163 10.9935 7.66548 11.2045 7.4545C11.4155 7.24353 11.7016 7.125 12 7.125C12.2984 7.125 12.5845 7.24353 12.7955 7.4545C13.0065 7.66548 13.125 7.95163 13.125 8.25L13.125 15.75C13.125 16.0484 13.0065 16.3345 12.7955 16.5455C12.5845 16.7565 12.2984 16.875 12 16.875Z" 
                                                        fill="#FF0068"
                                                        className={styles.verticalLine}
                                                    />
                                                </svg>
                                            ) : (
                                                // Original arrow for default theme
                                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path
                                                        d="M1.50635 6.91016L0.504394 7.91211L7.86963 15.2686L15.2261 7.91211L14.2329 6.91016L8.59033 12.5527L8.59033 0.731445L7.18408 0.731445L7.18408 12.5967L1.50635 6.91016Z"
                                                        fill="#FF0068"
                                                    />
                                                </svg>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className={`${styles.wrapperDescription} ${isExpanded ? styles.show : ''}`}>
                                    <div className={styles.description} dangerouslySetInnerHTML={{ __html: item.description || '' }}></div>
                                </div>
                            </div>
                        );
                    })}

                    {ctaButton?.text && theme !== 'type-2' && (
                        <Button className={styles.button} type="primary" theme="default" size="default" htmlType="button" link={ctaButton?.href}>
                            {ctaButton?.text || 'Book a Demo'}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default RoomStayBlogCustomerCaseAccordion;
